"use client";

import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import {
  HelpCircle,
  LogIn,
  LogOut,
  BellRing,
  Info,
  CircleDollarSign,
  Wallet,
} from "lucide-react";
import BookingIcon from "@/src/shared/components/icons/BookingIcon";
import InboxIcon from "@/src/shared/components/icons/InboxIcon";
import ProfileIcon from "@/src/shared/components/icons/ProfileIcon";
import WishlistIcon from "@/src/shared/components/icons/WishlistIcon";
import { clearAuth, getAuth } from "@/src/core/auth/session";
import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import { useLogin } from "@/src/core/auth/LoginContext";

interface IDropdownItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  description?: string;
  onClick?: (e?: React.MouseEvent) => void;
  href?: string;
  notificationCount?: number;
}

interface IAnimatedDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  items?: IDropdownItem[];
  className?: string;
  notificationCount?: number;
}

const ProfileDropdown: React.FC<IAnimatedDropdownProps> = ({
  isOpen,
  onClose,
  className = "",
  notificationCount = 0,
}) => {
  const router = useAppRouter();
  const { openLoginModal } = useLogin();
  const { user_profile, token } = getAuth();
  const isAuthenticated = !!(user_profile && token);

  const handleLogout = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    try {
      // Clear authentication data
      clearAuth();
      // Close dropdown
      onClose();
      // Redirect to home page
      await router.push("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleLoginClick = () => {
    onClose(); // Close dropdown first
    openLoginModal(); // Open login modal
  };

  const menuItems: IDropdownItem[] = useMemo(() => {
    const commonItems: IDropdownItem[] = [
      {
        id: "earn-hosting",
        label: "Earn by Hosting",
        icon: <CircleDollarSign size={20} />,
        href: "/earn-by-hosting",
      },
      {
        id: "help",
        label: "Help Center",
        icon: <HelpCircle size={20} />,
        href: "/contact-us",
      },
      {
        id: "about",
        label: "About Us",
        icon: <Info size={20} />,
        href: "/about",
      },
    ];

    if (isAuthenticated) {
      // Items for authenticated users
      return [
        {
          id: "account",
          label: "Account",
          description: "",
          icon: <ProfileIcon size={20} />,
          href: "/profile/edit",
        },
        {
          id: "messages",
          label: "Messages",
          icon: <InboxIcon size={20} />,
          href: "/chat",
        },
        {
          id: "my-bookings",
          label: "My booking",
          icon: <BookingIcon size={20} />,
          href: "/booking",
        },
        {
          id: "wishlists",
          label: "Wishlists",
          icon: <WishlistIcon size={20} />,
          href: "/wishlists",
        },
        {
          id: "wallet",
          label: "Wallet",
          icon: <Wallet size={20} />,
          href: "/wallet",
        },
        {
          id: "notifications",
          label: "Notifications",
          icon: <BellRing size={20} />,
          href: "/notifications",
          notificationCount:
            notificationCount > 0 ? notificationCount : undefined,
        },
        ...commonItems,
        {
          id: "logout",
          label: "Logout",
          icon: <LogOut size={20} />,
          onClick: handleLogout,
        },
      ];
    } else {
      return [
        {
          id: "login",
          label: "Log in or sign up",
          icon: <LogIn size={20} />,
          onClick: handleLoginClick,
        },
        ...commonItems,
      ];
    }
  }, [isAuthenticated, openLoginModal, notificationCount]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40"
            onClick={onClose}
          />

          {/* Dropdown Menu */}
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.95,
              y: -10,
              transformOrigin: "top right",
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              scale: 0.95,
              y: -10,
            }}
            transition={{
              duration: 0.2,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            className={`absolute top-full right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50 ${className}`}
          >
            <div className="py-2">
              {menuItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: index * 0.05,
                    duration: 0.2,
                  }}
                  className={
                    index === menuItems.length - 1 && item.id === "logout"
                      ? "border-t border-gray-200"
                      : ""
                  }
                >
                  {item.href ? (
                    <Link
                      href={item.href}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 flex items-start space-x-3 group"
                      onClick={() => onClose()}
                    >
                      {item.icon && (
                        <div className="text-ink-secondary group-hover:text-ink transition-colors mt-0.5 flex-shrink-0">
                          {item.icon}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-ink group-hover:text-black transition-colors">
                            {item.label}
                          </p>
                          {item.notificationCount &&
                            item.notificationCount > 0 && (
                              <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center ml-2 flex-shrink-0">
                                {item.notificationCount > 99
                                  ? "99+"
                                  : item.notificationCount}
                              </span>
                            )}
                        </div>
                        {item.description && (
                          <div className="text-sm text-ink-gray mt-1 group-hover:text-ink-secondary transition-colors">
                            {item.description}
                          </div>
                        )}
                      </div>
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={item.onClick}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 flex items-start space-x-3 group"
                    >
                      {item.icon && (
                        <div className="text-ink-secondary group-hover:text-ink transition-colors mt-0.5 flex-shrink-0">
                          {item.icon}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-ink group-hover:text-black transition-colors">
                            {item.label}
                          </p>
                          {item.notificationCount &&
                            item.notificationCount > 0 && (
                              <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center ml-2 flex-shrink-0">
                                {item.notificationCount > 99
                                  ? "99+"
                                  : item.notificationCount}
                              </span>
                            )}
                        </div>
                        {item.description && (
                          <div className="text-sm text-ink-gray mt-1 group-hover:text-ink-secondary transition-colors">
                            {item.description}
                          </div>
                        )}
                      </div>
                    </button>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ProfileDropdown;
